import React, { useState } from "react";
import axios from "axios";
import TopNav from "../../components/navigation/TopNav";
import TimeRangeSelector from "../../components/helpers/TimeRangeSelector";
import { useTranslation } from "react-i18next";

import StandardScreen, { ScreenContent } from "../../components/layout/StandardScreen";
import Stack from "../../components/layout/Stack";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import FormField from "../../components/forms/FormField";
import StatusMessage from "../../components/ui/StatusMessage";
import {
  TableShell,
  Table,
  Th,
  Td,
  NumberTd,
} from "../../components/data/Table";

const narrowShellStyle = {
  width: "100%",
  margin: "0 auto",
};


export default function UserTrafficReport() {
  const { t } = useTranslation();

  const [summary, setSummary] = useState([]);
  const [message, setMessage] = useState("");
  const [messageVariant, setMessageVariant] = useState("info");
  const [timeRange, setTimeRange] = useState({ start: "", end: "" });

  const fetchReport = async () => {
    if (!timeRange.start || !timeRange.end) return;

    axios.defaults.headers.common["Authorization"] = `Bearer ${localStorage.getItem("token")}`;

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/user/traffic-in-time`,
        {
          params: {
            start: timeRange.start,
            end: timeRange.end,
          },
        }
      );

      setSummary(res.data);

      if (res.data.length === 0) {
        setMessage(t("user_traffic_report.messages.empty"));
        setMessageVariant("info");
      } else {
        setMessage("");
        setMessageVariant("info");
      }
    } catch (err) {
      console.error("Failed to fetch summary", err);
      setSummary([]);
      setMessage(t("user_traffic_report.messages.fetch_failed"));
      setMessageVariant("error");
    }
  };

  return (
    <StandardScreen
      topNav={<TopNav title={t("user_traffic_report.title")} mode="main" />}
    >
      <ScreenContent>
        <div style={narrowShellStyle}>
          <Stack gap="4">

            {/* Filters */}
            <Card title={t("user_traffic_report.title")}>
              <Stack gap="3">

                <FormField>
                  <div>
                    <TimeRangeSelector onRangeChange={setTimeRange} />
                  </div>
                </FormField>

                <Button onClick={fetchReport} fullWidth>
                  {t("user_traffic_report.actions.get_report")}
                </Button>

                <StatusMessage variant={messageVariant} message={message} />
              </Stack>
            </Card>

            {/* Table */}
            {summary.length > 0 && (
              <Card>
                <TableShell maxHeight="65vh">
                  <Table>
                    <thead>
                      <tr>
                        <Th columnIndex={0} sticky wider>{t("user_traffic_report.table.vendor")} </Th>

                        <Th columnIndex={1} sticky wider> {t("user_traffic_report.table.wallet")} </Th>

                        <Th align="right" wider>{t("user_traffic_report.table.received_tickets")}</Th>

                        <Th align="right" wider>{t("user_traffic_report.table.sent_tickets")}</Th>

                        <Th align="right" wider>{t("user_traffic_report.table.net_ticket")}</Th>

                        <Th align="right" wider>{t("user_traffic_report.table.money_received")}</Th>

                        <Th align="right" wider>{t("user_traffic_report.table.money_paid")}</Th>

                        <Th align="right" wider>{t("user_traffic_report.table.net_money")}</Th>
                      </tr>
                    </thead>

                    <tbody>
                      {summary.map((item, index) => (
                        <tr key={`${item.wallet_id}-${index}`}>
                          <Td columnIndex={0} sticky>{item.vendor_name || "-"}</Td>
                          <Td columnIndex={1} sticky wider>{item.route_name || "-"}</Td>
                          <NumberTd>{item.sum_tickets_received}</NumberTd>
                          <NumberTd>{item.sum_tickets_paid}</NumberTd>
                          <NumberTd>{item.net_ticket_flow}</NumberTd>
                          <NumberTd>{item.sum_money_received}</NumberTd>
                          <NumberTd>{item.sum_money_paid}</NumberTd>
                          <NumberTd>{item.net_money_flow}</NumberTd>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </TableShell>
              </Card>
            )}

          </Stack>
        </div>
      </ScreenContent>
    </StandardScreen>
  );
}